import React, { ComponentType, FC } from 'react';
import styled, { CSSProperties } from 'styled-components';
import hoistNonReactStatics from 'hoist-non-react-statics';

export interface DecorateGridComponentProps<T> {
  as: ComponentType<T>;
  style: CSSProperties;
  component: FC<T>;
}

interface ChildGridComponentProps<T> {
  as: FC<ChildGridComponentProps<T>>;
  style: CSSProperties;
}

export default <T, >(displayName: string) => (WrappedComponent: FC<DecorateGridComponentProps<T>>) => {
  const StyledComponent = styled.div.withConfig({ displayName })<ChildGridComponentProps<T>>`
    ${({ style }) => style as string};
  `;

  class DecoratedComponent extends React.PureComponent<DecorateGridComponentProps<T>> {
    decoratedComponent: FC<ChildGridComponentProps<T>>;

    constructor(props: DecorateGridComponentProps<T>) {
      super(props);
      const { as, component, style: parentStyle } = props;
      // eslint-disable-next-line react/no-unstable-nested-components
      const FilteredComponent: FC<ChildGridComponentProps<T>> = ({ style, as: childAs, ...otherProps }) => (
        React.createElement<any>(as || component || 'div', otherProps)
      );

      // eslint-disable-next-line react/no-unstable-nested-components
      const DecoratedComponentImpl: FC<ChildGridComponentProps<T>> = ({ style, as: childAs, ...otherProps }) => (
        <StyledComponent
          as={FilteredComponent}
          {...otherProps}
          style={{ ...style, ...parentStyle }}
        />
      );

      this.decoratedComponent = DecoratedComponentImpl;
    }

    render() {
      const { as, component, ...props } = this.props;
      return React.createElement<any>(WrappedComponent, { component: this.decoratedComponent, ...props });
    }
  }

  return hoistNonReactStatics(DecoratedComponent, WrappedComponent);
};
